import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'
import { useSidebar } from '../context/SidebarContext'
import type { CrmPage } from './crmBridge'
import { AccountPic } from './CrmAccountSheet'
import { RailGlyph } from './CrmPageRail'
import { canWrite, FANSLY_MENU_SECTIONS, railEntryOn, railHref, railKey, railPages, type CrmPermissions } from './crmRail'
import './CrmTopBar.css'

interface Props {
  accountId: string
  page: CrmPage
  perms: CrmPermissions | null
  unread?: number
  /** The account's name, @username and picture (Fansly's logo spot and the account menu). */
  account?: { name: string; username?: string | null; avatarUrl?: string | null }
  /** Draws the account's picture with this class, in place of `account.avatarUrl` (the live Fansly one). */
  picture?: (className: string) => ReactNode
}

/** Home opens its post composer (New post on the bar). */
export const NEW_POST_QUERY = 'compose=1'

/** How many top bars are mounted (a page swap can briefly hold two). */
let mounted = 0

/**
 * The Fansly-style menu on a computer (Settings → Styling): fansly.com's top
 * bar instead of the CRM page rail. The account's name on the left goes Home,
 * as Fansly's logo does; on the right Messages, Notifications, Earnings and
 * New post, then the account picture opening the account menu with the rest,
 * in Fansly's sections. While it is up, html.has-crm-topbar moves the pages
 * under it (CrmTopBar.css). Phones keep the bottom tab bar.
 */
export function CrmTopBar({ accountId, page, perms, unread = 0, account, picture }: Props) {
  const { t } = useTranslation('library')
  const { search, pathname } = useLocation()
  const { sidebarWidth } = useSidebar()
  const [open, setOpen] = useState(false)
  const base = `/account/${encodeURIComponent(accountId)}/crm`
  const pages = railPages(perms, 'fansly')
  const lit = railEntryOn(pages, page, search, 'fansly')
  const home = pages.find((p) => p.page === 'home')
  const bar = pages.filter((p) => p.place === 'bar' && p !== home)
  const menu = FANSLY_MENU_SECTIONS.map((section) => ({ section, items: pages.filter((p) => p.place === section) })).filter((s) => s.items.length)
  const menuOn = lit !== null && lit.place !== 'bar'
  const newPost = canWrite(perms, 'queue')
  const name = account?.name ?? ''
  const pic = (className: string) => (picture ? picture(className) : <AccountPic name={name} url={account?.avatarUrl} className={className} />)

  useLayoutEffect(() => {
    const root = document.documentElement
    mounted += 1
    root.classList.add('has-crm-topbar')
    return () => {
      mounted -= 1
      if (mounted === 0) root.classList.remove('has-crm-topbar')
    }
  }, [])

  // The menu closes on a page change and on Esc.
  useEffect(() => setOpen(false), [pathname, search])
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  return (
    <header className="crm-topbar" style={{ left: sidebarWidth }}>
      {home ? (
        <Link to={railHref(base, home)} className={`crm-topbar-home ${lit === home ? 'is-on' : ''}`} title={t(home.labelKey)} aria-current={lit === home ? 'page' : undefined}>
          {pic('crm-topbar-pic')}
          <span className="crm-topbar-name">{name}</span>
        </Link>
      ) : (
        <span className="crm-topbar-home">
          <span className="crm-topbar-name">{name}</span>
        </span>
      )}
      <span className="crm-topbar-spacer" />
      <nav className="crm-topbar-icons" aria-label={t('crmRail.label')}>
        {bar.map((p) => {
          const on = p === lit
          const badge = p.page === 'chats' && unread > 0 ? (unread > 99 ? '99+' : String(unread)) : null
          return (
            <Link key={railKey(p)} to={railHref(base, p)} className={`crm-topbar-icon ${on ? 'is-on' : ''}`} title={t(p.labelKey)} aria-label={t(p.labelKey)} aria-current={on ? 'page' : undefined}>
              <RailGlyph id={p.icon} />
              {badge && <span className="crm-topbar-badge">{badge}</span>}
            </Link>
          )
        })}
      </nav>
      {newPost && (
        <Link to={`${base}/home?${NEW_POST_QUERY}`} className="crm-topbar-new" title={t('crmRail.fansly.newPost')}>
          <RailGlyph id="fansly-plus" />
          <span>{t('crmRail.fansly.newPost')}</span>
        </Link>
      )}
      {menu.length > 0 && (
        <div className="crm-topbar-me">
          <button
            type="button"
            className={`crm-topbar-avatar ${menuOn ? 'is-on' : ''} ${open ? 'is-open' : ''}`}
            aria-label={t('crmRail.fansly.accountMenu')}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            {pic('crm-topbar-pic crm-topbar-pic--me')}
            <RailGlyph id="fansly-chevron-down" />
          </button>
          {open && (
            <>
              <div className="crm-topbar-backdrop" onClick={() => setOpen(false)} />
              <div className="crm-topbar-menu" role="menu" aria-label={t('crmRail.fansly.accountMenu')}>
                <div className="crm-topbar-menu-head">
                  {pic('crm-topbar-pic crm-topbar-pic--lg')}
                  <span className="crm-topbar-menu-who">
                    <span className="crm-topbar-menu-name">{name}</span>
                    {account?.username && <span className="crm-topbar-menu-user">@{account.username}</span>}
                  </span>
                </div>
                {menu.map(({ section, items }) => (
                  <div key={section} className="crm-topbar-menu-section" role="group" aria-label={t(`crmRail.fansly.section.${section}`)}>
                    {section === 'creator' && <span className="crm-topbar-menu-label">{t('crmRail.fansly.section.creator')}</span>}
                    {items.map((p) => (
                      <Link
                        key={railKey(p)}
                        role="menuitem"
                        to={railHref(base, p)}
                        className={`crm-topbar-menu-item ${p === lit ? 'is-on' : ''}`}
                        aria-current={p === lit ? 'page' : undefined}
                        onClick={() => setOpen(false)}
                      >
                        <RailGlyph id={p.icon} />
                        {t(p.labelKey)}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </header>
  )
}
